import {
  resolveAttributionReferrerHost,
  resolveAttributionSourceFromSearchParams,
} from '@/analytics/source-attribution';

type AnalyticsAttributionTouch = {
  campaign: string | null;
  clickId: string | null;
  content: string | null;
  landingPage: string | null;
  medium: string | null;
  referrer: string | null;
  referrerHost: string | null;
  source: string | null;
  term: string | null;
};

type AnalyticsAttributionState = {
  firstTouch: AnalyticsAttributionTouch;
  lastTouch: AnalyticsAttributionTouch;
};

const ATTRIBUTION_STORAGE_KEY = 'imgenhancer.analytics.attribution.v3';

function normalizeText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readStoredState(): AnalyticsAttributionState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as AnalyticsAttributionState;
  } catch {
    return null;
  }
}

function writeStoredState(state: AnalyticsAttributionState) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage write failures.
  }
}

function resolveReferrer(params?: {
  currentHost?: string | null;
  referrer?: string | null;
}) {
  const referrer = normalizeText(
    params?.referrer ??
      (typeof document !== 'undefined' ? document.referrer : null)
  );
  const referrerHost = resolveAttributionReferrerHost(referrer);
  const currentHost = normalizeText(params?.currentHost)?.toLowerCase() ?? null;

  if (
    referrerHost &&
    currentHost &&
    referrerHost.toLowerCase() === currentHost
  ) {
    return {
      referrer: null,
      referrerHost: null,
    };
  }

  return {
    referrer,
    referrerHost,
  };
}

function buildTouch(params?: {
  currentHost?: string | null;
  fallbackDirect?: boolean;
  landingPage?: string | null;
  referrer?: string | null;
  searchParams?: URLSearchParams | null;
}): AnalyticsAttributionTouch | null {
  const searchParams =
    params?.searchParams ??
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null);
  const { referrer, referrerHost } = resolveReferrer(params);
  const sourceFromQuery =
    resolveAttributionSourceFromSearchParams(searchParams);
  const mediumFromQuery = normalizeText(searchParams?.get('utm_medium'));
  const campaignFromQuery = normalizeText(searchParams?.get('utm_campaign'));
  const contentFromQuery = normalizeText(searchParams?.get('utm_content'));
  const termFromQuery = normalizeText(searchParams?.get('utm_term'));
  const clickId =
    ['gclid', 'wbraid', 'gbraid', 'msclkid', 'fbclid'].flatMap((key) => {
      const value = normalizeText(searchParams?.get(key));
      return value ? [`${key}:${value}`] : [];
    })[0] ?? null;
  const landingPage = normalizeText(
    params?.landingPage ??
      (typeof window !== 'undefined' ? window.location.pathname : null)
  );

  const source = sourceFromQuery ?? referrerHost ?? null;
  const medium =
    mediumFromQuery ??
    (referrerHost ? 'referral' : params?.fallbackDirect ? 'direct' : null);
  const campaign = campaignFromQuery ?? null;
  const hasMeaningfulAttribution = Boolean(
    sourceFromQuery ||
      mediumFromQuery ||
      campaignFromQuery ||
      contentFromQuery ||
      termFromQuery ||
      clickId ||
      referrerHost
  );

  if (!hasMeaningfulAttribution && !params?.fallbackDirect) {
    return null;
  }

  return {
    source: source ?? 'direct',
    medium: medium ?? 'direct',
    campaign,
    clickId,
    content: contentFromQuery,
    landingPage,
    referrer,
    referrerHost,
    term: termFromQuery,
  };
}

export function readAnalyticsAttributionState() {
  return readStoredState();
}

export function syncAnalyticsAttributionState(params?: {
  currentHost?: string | null;
  landingPage?: string | null;
  referrer?: string | null;
  searchParams?: URLSearchParams | null;
}) {
  if (typeof window === 'undefined') {
    return null;
  }

  const currentHost = params?.currentHost ?? window.location.hostname ?? null;
  const currentTouch = buildTouch({
    currentHost,
    landingPage: params?.landingPage,
    referrer: params?.referrer,
    searchParams:
      params?.searchParams ?? new URLSearchParams(window.location.search),
  });
  const existing = readStoredState();

  if (!existing) {
    const directTouch =
      currentTouch ??
      buildTouch({
        currentHost,
        fallbackDirect: true,
        landingPage: params?.landingPage,
        referrer: params?.referrer,
        searchParams:
          params?.searchParams ?? new URLSearchParams(window.location.search),
      });

    if (!directTouch) {
      return null;
    }

    const nextState = {
      firstTouch: directTouch,
      lastTouch: directTouch,
    };
    writeStoredState(nextState);
    return nextState;
  }

  if (!currentTouch) {
    return existing;
  }

  const nextState = {
    firstTouch: existing.firstTouch,
    lastTouch: currentTouch,
  };
  writeStoredState(nextState);
  return nextState;
}

export function buildFallbackAnalyticsAttributionState(params?: {
  currentHost?: string | null;
  landingPage?: string | null;
  referrer?: string | null;
  searchParams?: URLSearchParams | null;
}) {
  const currentTouch =
    buildTouch({
      currentHost: params?.currentHost,
      landingPage: params?.landingPage,
      referrer: params?.referrer,
      searchParams: params?.searchParams,
    }) ??
    buildTouch({
      currentHost: params?.currentHost,
      fallbackDirect: true,
      landingPage: params?.landingPage,
      referrer: params?.referrer,
      searchParams: params?.searchParams,
    });

  if (!currentTouch) {
    return null;
  }

  return {
    firstTouch: currentTouch,
    lastTouch: currentTouch,
  } satisfies AnalyticsAttributionState;
}
